import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  Ruler,
  Search,
  RotateCcw,
  Plus,
  Save,
  Trash2,
  Pencil,
  CheckSquare,
  Square,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";

const UnitOfMeasurementMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uomList, setUomList] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [searchUomName, setSearchUomName] = useState("");
  const [searchUomCode, setSearchUomCode] = useState("");

  const [uomType, setUomType] = useState("base");
  const [baseUomOptions, setBaseUomOptions] = useState([]);
  const [baseUomId, setBaseUomId] = useState("");
  const [uomCode, setUomCode] = useState("");
  const [uomName, setUomName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [selectedUoms, setSelectedUoms] = useState([]);
  const [showForm, setShowForm] = useState(true);

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
    isDark
      ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
      : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
  }`;

  const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
    isDark ? "text-slate-300" : "text-slate-700"
  }`;

  const getItemId = (item) => {
    if (item.fld_isbase === true || item.fld_isbase === 1 || item.fld_isbase === "True") {
      return item.baseid || item.fld_uomid || item.uomid;
    }
    return item.childbaseid || item.fld_uomid || item.uomid;
  };

  const isBaseItem = (item) =>
    item.fld_isbase === true || item.fld_isbase === 1 || item.fld_isbase === "True";

  const getUomList = async (
    pg = 1,
    uomNameValue = searchUomName,
    uomCodeValue = searchUomCode
  ) => {
    try {
      setLoading(true);
      const res = await axios.get("https://nissi-india.com/api/uomGrid", {
        params: {
          page: pg,
          pagesize: limit,
          uomname: uomNameValue || "",
          uomcode: uomCodeValue || "",
        },
        headers: commonHeaders,
      });

      const response = res.data;
      setUomList(response.data || []);
      setTotalPages(response.totalPages || 1);
      setTotalRecords(response.totalRecords || 0);
    } catch (error) {
      console.error("Failed to fetch UOM list:", error);
      toast.error("Failed to fetch UOM list");
      setUomList([]);
    } finally {
      setLoading(false);
    }
  };

  const getBaseUomOptions = async () => {
    try {
      const res = await axios.get("https://nissi-india.com/api/item_uomList", {
        headers: commonHeaders,
      });
      setBaseUomOptions(Array.isArray(res.data.uomList) ? res.data.uomList : []);
    } catch (error) {
      console.error("Failed to load base UOM list:", error);
      toast.error("Failed to load base UOM list");
      setBaseUomOptions([]);
    }
  };

  useEffect(() => {
    getUomList(page);
  }, [page, commonHeaders]);

  const handleSearch = () => {
    setPage(1);
    getUomList(1);
  };

  const handleReset = () => {
    setSearchUomName("");
    setSearchUomCode("");
    setPage(1);
    getUomList(1, "", "");
  };

  useEffect(() => {
    getBaseUomOptions();
  }, [commonHeaders]);

  const handleSelect = (item) => {
    const id = getItemId(item);
    const exists = selectedUoms.find((u) => u.id === id);

    if (exists) {
      setSelectedUoms((prev) => prev.filter((u) => u.id !== id));
    } else {
      setSelectedUoms((prev) => [
        ...prev,
        { ...item, id, uomid: id, isBase: isBaseItem(item) },
      ]);
    }
  };

  const handleEdit = (item) => {
    const id = getItemId(item);
    setEditItem({ ...item, id, uomid: id });

    const baseFlag = isBaseItem(item);
    setUomType(baseFlag ? "base" : "child");
    setUomName(item.childuom || item.baseuom || "");
    setUomCode(item.childcode || item.basecode || "");
    setRemarks(item.fld_remarks || "");
    setBaseUomId(baseFlag ? "" : item.baseid || "");
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditItem(null);
    setBaseUomId("");
    setUomName("");
    setUomCode("");
    setRemarks("");
    setUomType("base");
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!uomName.trim()) {
      toast.warning("UOM Name is required");
      return;
    }
    if (!uomCode.trim()) {
      toast.warning("UOM Code is required");
      return;
    }
    if (uomType === "child" && !baseUomId) {
      toast.warning("Please select Base UOM");
      return;
    }

    try {
      const payload = {
        isbase: uomType === "base" ? 1 : 0,
        baseid: uomType === "base" ? 0 : parseInt(baseUomId, 10),
        uomname: uomName.trim(),
        uomcode: uomCode.trim(),
        userid: parseInt(userId || 0, 10),
        remarks: remarks.trim(),
      };

      if (!payload.userid) {
        toast.warning("User ID not found in localStorage");
        return;
      }

      if (editItem && editItem.id !== undefined) {
        await axios.put(`https://nissi-india.com/api/uom_update/${editItem.id}`, payload, {
          headers: commonHeaders,
        });
        toast.success("UOM updated successfully");
      } else {
        await axios.post("https://nissi-india.com/api/uom_insert", payload, {
          headers: commonHeaders,
        });
        toast.success("UOM added successfully");
      }

      handleAddNew();
      getUomList(page);
      getBaseUomOptions();
    } catch (err) {
      console.error("Save error:", err);
      toast.error(err?.response?.data?.error || "Failed to save UOM");
    }
  };

const handleDeleteClick = () => {
  if (selectedUoms.length === 0) {
    toast.warning("No items selected");
    return;
  }
  setShowDeleteConfirm(true);
};

const confirmDeleteSelected = async () => {
  setShowDeleteConfirm(false);

  try {
    const payload = selectedUoms.map((item) => ({
      uomid: item.uomid || item.id,
      isBase: item.isBase,
    }));

    await axios.delete("https://nissi-india.com/api/uom_delete", {
      headers: commonHeaders,
      data: { selectedUoms: payload },
    });

    toast.success("Selected UOMs deleted successfully");
    setSelectedUoms([]);
    getUomList(page);
    getBaseUomOptions();
  } catch (error) {
    console.error("Batch delete error:", error);
    toast.error(error?.response?.data?.error || "Failed to delete selected UOMs");
  }
};

  return (
    <LayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">UOM Master</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Form Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* Form Header */}
          <div
            className={`px-6 py-4 border-b flex items-center justify-between ${
              isDark ? "border-slate-700/50 bg-slate-900/40" : "border-slate-200/50 bg-white/60"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
              >
                <Ruler className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className={`text-lg font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
                  {editItem ? "Edit UOM" : "Add New UOM"}
                </h3>
                <p className={`text-xs ${isDark ? "text-slate-400" : "text-slate-600"}`}>
                  {editItem ? "Update unit of measurement" : "Create base or child UOM"}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowForm(!showForm)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition ${
                isDark
                  ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                  : "border-slate-300 text-slate-700 hover:bg-slate-100"
              }`}
            >
              {showForm ? "Hide Form" : "Show Form"}
            </button>
          </div>

          {showForm && (
            <div className="p-6">
              {/* UOM Type Radio */}
              <div className="flex items-center gap-6 mb-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="base"
                    checked={uomType === "base"}
                    onChange={() => setUomType("base")}
                    className={`w-4 h-4 accent-${baseColor}-500`}
                  />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                    Base UOM
                  </span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    value="child"
                    checked={uomType === "child"}
                    onChange={() => setUomType("child")}
                    className={`w-4 h-4 accent-${baseColor}-500`}
                  />
                  <span className={`text-sm font-medium ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                    Child UOM
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>Base UOM Name</label>
                  <select
                    className={`${inputClass} ${uomType === "base" ? "opacity-60 cursor-not-allowed" : ""}`}
                    value={baseUomId}
                    onChange={(e) => setBaseUomId(e.target.value)}
                    disabled={uomType === "base"}
                  >
                    <option value="">Select Base UOM</option>
                    {baseUomOptions.map((item, idx) => (
                      <option key={item.fld_uomid || idx} value={item.fld_uomid}>
                        {item.fld_uomname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>
                    UOM Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={uomName}
                    onChange={(e) => setUomName(e.target.value)}
                    placeholder="Enter UOM name"
                  />
                </div>

                <div>
                  <label className={labelClass}>
                    UOM Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    className={inputClass}
                    value={uomCode}
                    onChange={(e) => setUomCode(e.target.value)}
                    placeholder="Enter UOM code"
                  />
                </div>

                <div>
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    className={`${inputClass} resize-none`}
                    rows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Optional remarks..."
                  />
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex flex-wrap justify-end gap-3 mt-6 pt-5 border-t border-slate-200 dark:border-slate-700">
                {/* <button
                  onClick={handleAddNew}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
                    isDark
                      ? "border-slate-600 text-slate-300 hover:bg-slate-800"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <Plus size={16} />
                  Add New
                </button> */}

                <button
                  onClick={handleSave}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                >
                  <Save size={16} />
                  {editItem ? "Update UOM" : "Save UOM"}
                </button>
              </div>
            </div>
          )}
        </div>

               {/* Table Card */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          {/* Search Bar */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={labelClass}>UOM Name</label>
                <input
                  type="text"
                  value={searchUomName}
                  onChange={(e) => setSearchUomName(e.target.value)}
                  placeholder="Search by UOM name"
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>UOM Code</label>
                <input
                  type="text"
                  value={searchUomCode}
                  onChange={(e) => setSearchUomCode(e.target.value)}
                  placeholder="Search by UOM code"
                  className={inputClass}
                />
              </div>

              <div className="flex items-end gap-2 lg:col-span-2">
                <button
                  onClick={handleSearch}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700`}
                >
                  <Search size={16} />
                  Search
                </button>
                <button
                  onClick={handleReset}
                  title="Clear filters"
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg border transition ${
                    isDark
                      ? "border-slate-600 text-slate-200 hover:bg-slate-700/50"
                      : "border-slate-300 text-slate-700 hover:bg-slate-100"
                  }`}
                >
                  <RotateCcw size={16} />
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
                <h3 className="text-xl font-bold">UOM List</h3>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    isDark ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  {totalRecords} records
                </span>
              </div>

              <button
  onClick={handleDeleteClick}
  disabled={selectedUoms.length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow transition ${
                  selectedUoms.length > 0
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-gray-400 cursor-not-allowed"
                }`}
              >
                <Trash2 size={16} />
                Delete ({selectedUoms.length})
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">S.No</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Base UOM</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Base Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Child UOM</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Child Code</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Select</th>
                  <th className="px-4 py-3 text-center text-xs font-bold uppercase">Edit</th>
                </tr>
              </thead>

             <tbody className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}>
  {loading ? (
    [...Array(5)].map((_, i) => (
      <tr key={i}>
        {[...Array(7)].map((_, j) => (
          <td key={j} className="px-4 py-3">
            <Skeleton height={20} baseColor={isDark ? "#374151" : "#f3f4f6"} />
          </td>
        ))}
      </tr>
    ))
  ) : uomList.length === 0 ? (
    <tr>
      <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
        <div className="flex flex-col items-center gap-2">
          <Ruler size={40} className="text-gray-400" />
          <span>No UOM records found</span>
        </div>
      </td>
    </tr>
  ) : (
    uomList.map((item, index) => {
      const id = getItemId(item);
      const isSelected = selectedUoms.find((u) => u.id === id);

      return (
        <tr
          key={id || index}
          className={`transition ${
            isDark
              ? "bg-gray-800/20 hover:bg-gray-700/30"
              : "bg-white hover:bg-gray-50"
          } ${isSelected ? `bg-${baseColor}-50 dark:bg-${baseColor}-900/20` : ""}`}
        >
          {/* Correct continuous serial number */}
          <td className="px-4 py-3 font-medium">
            {(page - 1) * limit + index + 1}
          </td>

          <td className="px-4 py-3 font-medium">{item.baseuom}</td>

          <td className="px-4 py-3">
            <span
              className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${
                isDark
                  ? "bg-gray-700 text-gray-200"
                  : `bg-${baseColor}-100 text-${baseColor}-800`
              }`}
            >
              {item.basecode}
            </span>
          </td>

          <td className="px-4 py-3">{item.childuom || "-"}</td>
          <td className="px-4 py-3">{item.childcode || "-"}</td>

          <td className="px-4 py-3 text-center">
            <button
              onClick={() => handleSelect(item)}
              className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            >
              {isSelected ? (
                <CheckSquare size={18} className="text-green-600" />
              ) : (
                <Square size={18} className="text-gray-400" />
              )}
            </button>
          </td>

          <td className="px-4 py-3 text-center">
            <button
              onClick={() => handleEdit(item)}
              className="p-1.5 rounded-full bg-yellow-100 text-yellow-600 hover:bg-yellow-200 transition"
              title="Edit"
            >
              <Pencil size={16} />
            </button>
          </td>
        </tr>
      );
    })
  )}
</tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {totalRecords > 0
                  ? `Showing ${(page - 1) * limit + 1} to ${Math.min(
                      page * limit,
                      totalRecords
                    )} of ${totalRecords} results`
                  : "Showing 0 results"}
              </div>

              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(p - 1, 1))}
                  disabled={page === 1}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>

                {[...Array(Math.min(totalPages, 5))].map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setPage(idx + 1)}
                    className={`px-4 py-2 text-sm font-medium transition ${
                      page === idx + 1
                        ? `bg-${baseColor}-${baseShade} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                    }`}
                  >
                    {idx + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                  disabled={page === totalPages || totalPages === 0}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium ${
                    page === totalPages || totalPages === 0
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />
      <ConfirmModal
  open={showDeleteConfirm}
  title="Delete UOMs"
  message={`Are you sure you want to delete ${selectedUoms.length} UOM(s)? This action cannot be undone.`}
  confirmLabel="Delete"
  onConfirm={confirmDeleteSelected}
  onCancel={() => setShowDeleteConfirm(false)}
/>
    </LayoutWrapper>
  );
};

export default UnitOfMeasurementMaster;